import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { MdDeleteForever, MdMoreVert, MdNoteAdd, MdPlaylistAdd, MdDownload, MdDescription, MdUpload, MdGeneratingTokens, MdOutlineContentCopy, MdSearch } from "react-icons/md";
import { Menu } from "@headlessui/react";
import SelectUser from "@/components/selectUser";
import api from "@/services/api";
import { readFileAsync } from "@/utils";

export default function DataRoomTab({ answer, setAnswer }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [files, setFiles] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState({ search: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getFiles();
  }, [filter]);

  async function getFiles() {
    try {
      setLoading(true);
      const { ok, data, total } = await api.post(`/mission_ao_file/search`, {
        ...filter,
        mission_ao_answer_id: answer._id,
        limit: 1000,
      });
      if (!ok) throw new Error("Failed to fetch files");
      setFiles(data);
      setTotal(total);
    } catch (error) {
      console.error(error);
      toast.error("Error fetching files");
    } finally {
      setLoading(false);
    }
  }

  if (!selectedFile)
    return (
      <Documents
        files={files}
        answer={answer}
        setAnswer={setAnswer}
        onChange={getFiles}
        onSelectFile={setSelectedFile}
        selectedFile={selectedFile}
        filter={filter}
        setFilter={setFilter}
        total={total}
        loading={loading}
      />
    );

  return (
    <div className="p-6">
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="flex-1 flex flex-col bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Détails du document</h2>
            <button onClick={() => setSelectedFile(null)} className="text-gray-500 hover:text-gray-700 px-3 py-1 rounded-md hover:bg-gray-100 transition-colors">
              Retour à la liste
            </button>
          </div>
          <Edit file={selectedFile} getFiles={getFiles} setSelectedFile={setSelectedFile} />
        </div>
        <div className="lg:w-1/2">
          <FilePreview file={selectedFile} />
        </div>
      </div>
    </div>
  );
}

const Edit = ({ file, getFiles, setSelectedFile }) => {
  const [values, setValues] = useState({ user_id: "", user_name: "", user_avatar: "", comment: "" });
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    setValues(file);
  }, [file]);

  const handleSave = async () => {
    try {
      const { ok, data } = await api.put(`/mission_ao_file/${file._id}`, values);
      if (!ok) throw new Error("Failed to update file");
      toast.success("Document mis à jour!");
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleGenerateSummary = async () => {
    try {
      setGenerating(true);
      toast.loading("Génération du résumé en cours...");
      const { ok, data } = await api.put(`/mission_ao_file/${file._id}/summary`);
      if (!ok) throw new Error("Failed to generate summary");
      toast.dismiss();
      setSelectedFile(data);
      await getFiles();
      toast.success("Résumé généré avec succès!");
    } catch (error) {
      console.error(error);
      toast.dismiss();
      toast.error("Erreur: seuls les fichiers PDF sont supportés");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Nom du document</label>
        <div className="text-sm bg-gray-50 p-3 rounded-lg border border-gray-200 text-gray-800 font-medium">{file.label}</div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Responsable du document</label>
        <SelectUser
          value={{ _id: values.user_id, name: values.user_name, avatar: values.user_avatar }}
          onChange={(user) => setValues({ ...values, user_id: user._id, user_name: user.name, user_avatar: user.avatar })}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Commentaires et notes</label>
        <textarea
          className="w-full rounded-lg border border-gray-300 p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-700 min-h-[120px]"
          value={values.comment}
          onChange={(e) => setValues({ ...values, comment: e.target.value })}
          placeholder="Ajoutez vos commentaires concernant ce document..."
        />
      </div>

      <div className="flex flex-wrap gap-3">
        <button
          onClick={handleGenerateSummary}
          disabled={generating}
          className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
          <MdGeneratingTokens className="mr-2 h-5 w-5" />
          {generating ? "Génération en cours..." : "Générer un résumé avec l'IA"}
        </button>

        <a
          href={file.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors">
          <MdDownload className="mr-2 h-5 w-5" />
          Télécharger
        </a>

        <button
          onClick={handleSave}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors">
          Enregistrer les modifications
        </button>
      </div>

      {file.summary && (
        <div className="mt-6 border-t border-gray-200 pt-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-medium text-gray-900 flex items-center">
              <MdDescription className="mr-2 h-5 w-5 text-indigo-500" />
              Résumé généré par l'IA
            </h3>
            <button
              onClick={() => {
                navigator.clipboard.writeText(file.summary);
                toast.success("Résumé copié!");
              }}
              className="text-sm text-gray-500 hover:text-gray-700 flex items-center">
              <MdOutlineContentCopy className="mr-1 h-4 w-4" />
              Copier
            </button>
          </div>
          <div className="w-full rounded-lg border border-gray-200 p-4 bg-gray-50 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: file.summary }} />
        </div>
      )}
    </div>
  );
};

const Documents = ({ files, onChange, answer, setAnswer, onSelectFile, selectedFile, filter, setFilter, total, loading }) => {
  const handleFileUpload = async (e) => {
    const uploadedFiles = e.target.files;
    if (!uploadedFiles.length) return;

    const toastId = toast.loading(`Chargement de ${uploadedFiles.length} fichier${uploadedFiles.length > 1 ? "s" : ""}...`);

    for (let file of uploadedFiles) {
      try {
        const rawBody = await readFileAsync(file);
        const { data } = await api.post(`/file`, { file: { rawBody, name: file.name }, folder: "answers" });

        await api.post(`/mission_ao_file`, {
          mission_ao_answer_id: answer._id,
          mission_ao_answer_name: answer.name,
          label: file.name,
          url: data,
          type: file.type,
        });
      } catch (error) {
        console.error(error);
        toast.error(`Erreur: ${file.name}`);
      }
    }

    toast.dismiss(toastId);
    await onChange();
    toast.success("Fichiers importés avec succès!");
  };

  const handleDelete = async (fileId) => {
    try {
      if (!confirm("Êtes-vous sûr de vouloir supprimer ce fichier?")) return;

      const { ok } = await api.remove(`/mission_ao_file/${fileId}`);
      if (!ok) throw new Error("Failed to update files");
      toast.success("Fichier supprimé!");
      await onChange();
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleFillNotation = async (file) => {
    try {
      toast.loading("Remplissage de la notation...");
      const { ok, data } = await api.post(`/mission_ao_answer/${answer._id}/fill-notation`, { url: file.url });
      if (!ok) throw new Error("Failed to fill notation");
      toast.dismiss();
      toast.success("Notation remplie avec succès!");
      setAnswer(data);
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors du remplissage");
    }
  };

  const handleFillTodo = async (file) => {
    try {
      toast.loading("Remplissage de la todo list...");
      const { ok, data } = await api.post(`/mission_ao_answer/${answer._id}/fill-todo`, { url: file.url });
      if (!ok) throw new Error("Failed to fill todo");
      toast.dismiss();
      toast.success("Todo list remplie avec succès!");
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors du remplissage");
    }
  };

  const handleFillCCTP = async (file) => {
    try {
      toast.loading("Remplissage de l'éditeur CCTP...");
      const { ok, data } = await api.put(`/mission_ao_answer/${answer._id}/cctp-pdf`, { url: file.url });
      if (!ok) throw new Error("Failed to fill CCTP");
      toast.dismiss();
      toast.success("CCTP rempli avec succès!");
      setAnswer(data);
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors du remplissage du CCTP");
    }
  };

  const handleDownload = async (file) => {
    try {
      window.open(file.url, "_blank");
    } catch (error) {
      console.error("Failed to open file:", error);
      toast.error(`Erreur lors de l'ouverture du fichier: ${error.message}`);
    }
  };

  // Group files by type
  const filesByType = files.reduce((acc, file) => {
    const type = file.type ? file.type.split("/")[0] : "other";
    const category = type === "application" ? "documents" : type === "image" ? "images" : type === "text" ? "textes" : "autres";

    if (!acc[category]) acc[category] = [];
    acc[category].push(file);
    return acc;
  }, {});

  return (
    <div className="p-6">
      {/* Header with upload and search */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{answer.title}</h2>
            <p className="text-sm text-gray-500">Dataroom - {total} documents</p>
          </div>

          <div className="flex flex-1 md:flex-none items-center gap-3">
            <div className="relative flex-1 md:w-64">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MdSearch className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Rechercher un document..."
                value={filter.search}
                onChange={(e) => setFilter({ ...filter, search: e.target.value })}
              />
            </div>

            <div>
              <label
                htmlFor="multi-file-upload"
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer transition-colors">
                <MdUpload className="mr-2 h-5 w-5" />
                Importer
              </label>
              <input id="multi-file-upload" type="file" multiple className="hidden" onChange={handleFileUpload} />
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center p-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : files.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <div className="mx-auto w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-4">
            <MdDescription className="h-10 w-10 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun document disponible</h3>
          <p className="text-gray-500 max-w-md mx-auto mb-6">Importez des documents pour commencer à alimenter votre dataroom et utiliser l'analyse par l'IA.</p>
          <label htmlFor="empty-file-upload" className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer transition-colors">
            <MdUpload className="mr-2 h-5 w-5" />
            Importer des documents
          </label>
          <input id="empty-file-upload" type="file" multiple className="hidden" onChange={handleFileUpload} />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {Object.entries(filesByType).map(([category, categoryFiles]) => (
            <div key={category} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                <h3 className="text-sm font-medium text-gray-700 capitalize">
                  {category} ({categoryFiles.length})
                </h3>
              </div>
              <div className="divide-y divide-gray-200">
                {categoryFiles.map((file) => (
                  <div key={file._id} className="px-4 py-3 hover:bg-gray-50 flex items-center transition-colors">
                    <div className="flex-1 min-w-0 cursor-pointer pr-4" onClick={() => onSelectFile(file)}>
                      <div className="flex items-center">
                        <MdDescription className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0" />
                        <p className="text-sm font-medium text-gray-900 truncate">{file.label}</p>
                      </div>
                      {file.comment && <p className="mt-1 text-xs text-gray-500 truncate ml-7">{file.comment}</p>}
                      {file.summary && (
                        <div className="ml-7 mt-1 flex items-center">
                          <span className="px-2 py-0.5 bg-indigo-100 text-indigo-800 text-xs rounded-full">Résumé AI disponible</span>
                        </div>
                      )}
                    </div>

                    {file.user_name && (
                      <div className="flex items-center gap-2 mr-4">
                        {file.user_avatar && <img src={file.user_avatar} alt={file.user_name} className="w-6 h-6 rounded-full" />}
                        <span className="text-xs text-gray-500 hidden md:block">{file.user_name}</span>
                      </div>
                    )}

                    <Menu as="div" className="relative inline-block text-left">
                      <Menu.Button className="p-1.5 hover:bg-gray-100 rounded-full">
                        <MdMoreVert className="text-gray-500 w-5 h-5" />
                      </Menu.Button>
                      <Menu.Items className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg z-10 ring-1 ring-black ring-opacity-5 divide-y divide-gray-100">
                        <div className="py-1">
                          <Menu.Item>
                            {({ active }) => (
                              <button onClick={() => handleDownload(file)} className={`${active ? "bg-gray-100" : ""} flex w-full items-center px-4 py-2 text-sm text-gray-700`}>
                                <MdDownload className="mr-3 h-5 w-5 text-gray-500" />
                                Télécharger
                              </button>
                            )}
                          </Menu.Item>
                          <Menu.Item>
                            {({ active }) => (
                              <button onClick={() => onSelectFile(file)} className={`${active ? "bg-gray-100" : ""} flex w-full items-center px-4 py-2 text-sm text-gray-700`}>
                                <MdNoteAdd className="mr-3 h-5 w-5 text-gray-500" />
                                Éditer / Voir détails
                              </button>
                            )}
                          </Menu.Item>
                        </div>
                        <div className="py-1">
                          <Menu.Item>
                            {({ active }) => (
                              <button
                                onClick={() => handleFillNotation(file)}
                                className={`${active ? "bg-gray-100" : ""} flex w-full items-center px-4 py-2 text-sm text-gray-700`}>
                                <MdPlaylistAdd className="mr-3 h-5 w-5 text-indigo-500" />
                                Remplir la notation
                              </button>
                            )}
                          </Menu.Item>
                          <Menu.Item>
                            {({ active }) => (
                              <button onClick={() => handleFillTodo(file)} className={`${active ? "bg-gray-100" : ""} flex w-full items-center px-4 py-2 text-sm text-gray-700`}>
                                <MdPlaylistAdd className="mr-3 h-5 w-5 text-blue-500" />
                                Remplir la todo liste
                              </button>
                            )}
                          </Menu.Item>
                          <Menu.Item>
                            {({ active }) => (
                              <button onClick={() => handleFillCCTP(file)} className={`${active ? "bg-gray-100" : ""} flex w-full items-center px-4 py-2 text-sm text-gray-700`}>
                                <MdPlaylistAdd className="mr-3 h-5 w-5 text-green-500" />
                                Remplir le CCTP
                              </button>
                            )}
                          </Menu.Item>
                        </div>
                        <div className="py-1">
                          <Menu.Item>
                            {({ active }) => (
                              <button onClick={() => handleDelete(file._id)} className={`${active ? "bg-red-50" : ""} flex w-full items-center px-4 py-2 text-sm text-red-600`}>
                                <MdDeleteForever className="mr-3 h-5 w-5 text-red-500" />
                                Supprimer
                              </button>
                            )}
                          </Menu.Item>
                        </div>
                      </Menu.Items>
                    </Menu>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const FilePreview = ({ file }) => {
  if (!file) return null;

  const fileType = file.type || "";
  const isImage = fileType.startsWith("image/");
  const isPdf = fileType === "application/pdf";

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden h-full min-h-[400px] flex flex-col">
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
        <h3 className="text-base font-medium text-gray-700">Aperçu</h3>
        <a href={file.url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 transition-colors">
          Ouvrir
        </a>
      </div>
      <div className="flex-1 flex items-center justify-center p-4 bg-gray-50">
        {isImage ? (
          <img src={file.url} alt={file.label} className="max-w-full max-h-[600px] object-contain" />
        ) : isPdf ? (
          <iframe src={file.url} className="w-full h-full min-h-[600px]" title={file.label}></iframe>
        ) : (
          <div className="text-center p-8">
            <MdDescription className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-2">L'aperçu n'est pas disponible pour ce type de fichier</p>
            <a
              href={file.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors">
              <MdDownload className="mr-2 h-4 w-4" />
              Télécharger
            </a>
          </div>
        )}
      </div>
    </div>
  );
};
